import { ErrorCode, IBrand } from '../utils/entities/interface';
import { Pagination } from './booking';
import { get, post } from './https';

interface BrandListResponse {
  status: boolean;
  data: IBrand[];
  count: number;
  pagination: Pagination;
}

const GETBrands = async ({
  page = 1,
  limit = 10,
  searchQuery = '',
  sortField = 'name',
  sortOrder = 'ASC',
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
}): Promise<BrandListResponse> => {
  // Initialize the base URL
  let url = `/api/v1/admin/brand-list?limit=${limit}&page=${page}`;

  // Conditionally add search parameter if provided
  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  // Conditionally add sortField and sortOrder if they are provided
  if (sortField && sortField !== 'name') {
    // assuming 'name' is the default field, you can change it if needed
    url += `&sortField=${sortField}`;
  }

  if (sortOrder && sortOrder !== 'ASC') {
    // assuming 'ASC' is the default sort order
    url += `&sortOrder=${sortOrder}`;
  }

  // Fetch the response
  const response = await get(url); // Use the previously defined `get` function

  if (response.status == 200) {
    return response.data; // If status is true, return the user data
  } else {
    throw new Error('Failed to fetch users');
  }
};

const AddBrandService = async ({
  name,
  brandLogoUrl,
}: {
  name: string;
  brandLogoUrl?: string;
}): Promise<number> => {
  const response = await post('/api/v1/admin/brand/create-edit', {
    name,
    ...(brandLogoUrl && { brandLogoUrl }),
  });
  if (response.status == 200) {
    return 200; // Return success message (User activated/deactivated)
  } else {
    throw new Error(response.statusText || 'Failed to create brand');
  }
};
const ToggleBrandStatus = async (brandId: string): Promise<string> => {
  // Call the API endpoint to toggle Brand active/inactive status
  const response = await get(`/api/v1/admin/brand/active-inactive/${brandId}`);

  if (response.status == 200) {
    return response.data; // Return success message (User activated/deactivated)
  } else {
    throw new Error(response.statusText || 'Failed to change Brand status');
  }
};

interface ErrorCodesResponse {
  status: boolean;
  data: ErrorCode[];
  count: number;
  pagination: Pagination;
}

const GETErrorCodes = async ({
  page = 1,
  limit = 10,
  searchQuery = '',
  sortField = 'name',
  sortOrder = 'ASC',
  brandId = '',
  category,
  acType,
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
  brandId: string;
  category?: string;
  acType?: string;
}): Promise<ErrorCodesResponse> => {
  // Initialize the base URL
  let url = `/api/v1/admin/error-code-list/${brandId}?limit=${limit}&page=${page}`;

  // Conditionally add search parameter if provided
  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  // Conditionally add sortField and sortOrder if they are provided
  if (sortField && sortField !== 'name') {
    // assuming 'name' is the default field, you can change it if needed
    url += `&sortField=${sortField}`;
  }

  if (acType) {
    url += `&acType=${acType}`;
  }
  if (sortOrder && sortOrder !== 'ASC') {
    // assuming 'ASC' is the default sort order
    url += `&sortOrder=${sortOrder}`;
  }

  if (category) {
    url += `&category=${category}`;
  }
  // Fetch the response
  const response = await get(url); // Use the previously defined `get` function

  if (response.status == 200) {
    return response.data; // If status is true, return the user data
  } else {
    throw new Error('Failed to fetch Brands');
  }
};
// const { brandId, errorCodeId, acType, models, code, solution } = req.body;

const CreateErrorCode = async ({
  acType,
  models,
  code,
  solution,
  brandId,
  category,
  description,
}: {
  acType: string;
  models: string;
  code: string;
  solution: string[];
  brandId: string;
  category: 'INVERTOR' | 'NON_INVERTOR';
  description: string;
}): Promise<number> => {
  const response = await post('/api/v1/admin/error-code/create-edit', {
    acType,
    models,
    code,
    brandId,
    category,
    description,
    solution,
  });
  if (response.status == 200) {
    return 200; // Return success message (User activated/deactivated)
  } else {
    throw new Error(response.statusText || 'Failed to create user');
  }
};

interface IEditErrorCodeProps {
  brandId: string;
  errorCodeId: string;
  acType: string;
  models: string;
  code: string;
  solution: string[];
  category: 'INVERTOR' | 'NON_INVERTOR';
  description: string;
}
const UpdateErrorCode = async ({
  acType,
  models,
  code,
  brandId,
  solution,
  errorCodeId,
  category,
  description,
}: IEditErrorCodeProps) => {
  try {
    const response = await post('/api/v1/admin/error-code/create-edit', {
      acType,
      models,
      code,
      brandId,
      solution,
      errorCodeId,
      category,
      description,
    });
    if (response.status == 200) {
      return 200; // Return success message (User activated/deactivated)
    } else {
      throw new Error(response.statusText || 'Failed to Update Error code ');
    }
  } catch (error) {
    console.error('Error updating error code:', error);
    throw error;
  }
};

export interface UploadResponse {
  message: string;
  missingColumns?: string[];
  error?: string;
}

const UploadErrorCodeExcel = async (file: File): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await post(
      '/api/v1/admin/error-code-excel-upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Failed to upload file');
    }
  } catch (error: any) {
    throw new Error(error.message || 'An unknown error occurred');
  }
};

const DeleteErrorCode = async ({
  brandId,
  errorCodeId,
}: {
  brandId: string;
  errorCodeId: string;
}): Promise<number> => {
  const response = await post('/api/v1/admin/error-code/delete', {
    brandId,
    errorCodeId,
  });

  if (response.status === 200) {
    return 200;
  }

  throw new Error(response.data?.message || 'Failed to delete error code');
};

export {
  GETBrands,
  GETErrorCodes,
  ToggleBrandStatus,
  AddBrandService,
  CreateErrorCode,
  UploadErrorCodeExcel,
  UpdateErrorCode,
  DeleteErrorCode,
};
